import { Component } from '@theme/component';

/**
 * @typedef {Object} AmountVariant
 * @property {number} id - The variant id.
 * @property {string} money - The pre-formatted price, rendered by Liquid in the active currency.
 * @property {boolean} available - Whether the variant can be purchased.
 */

/**
 * @typedef {Object} GiftCardAmountRefs
 * @property {HTMLScriptElement} variantData - JSON lookup table, keyed by price in cents.
 * @property {HTMLInputElement} slider - The amount range input.
 * @property {HTMLButtonElement} decrease - Step down control.
 * @property {HTMLButtonElement} increase - Step up control.
 * @property {HTMLParagraphElement} [notice] - Message shown when an amount has no purchasable variant.
 * @property {HTMLDivElement} [liveRegion] - Visually-hidden region announcing that message.
 */

const URL_SYNC_DELAY = 250;

/**
 * Maps an amount slider onto the gift card product's existing variants.
 *
 * The slider works in whole dollars so the browser enforces the increment; each stop is resolved
 * back to a variant through a cents-keyed lookup. Selecting an amount writes straight to the
 * product form's hidden `input[name="id"]` and rewrites the price block's text.
 *
 * It intentionally does not dispatch variant events — see the block's doc comment.
 *
 * @extends {Component<GiftCardAmountRefs>}
 */
class GiftCardAmount extends Component {
  requiredRefs = ['variantData', 'slider', 'decrease', 'increase'];

  /** @type {Record<string, AmountVariant>} */
  #variants = {};

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #urlTimer;

  /** Whether this component is the reason Add to Cart is currently disabled. */
  #disabledAddToCart = false;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#variants = JSON.parse(this.refs.variantData.textContent || '{}');
    } catch (error) {
      console.error('[gift-card-amount] Could not parse variant data', error);
      this.#variants = {};
    }

    // Re-assert on load. The server may have rendered a variant outside the configured window
    // (a stale ?variant= link), in which case the slider was clamped and the form's hidden input
    // still points at the un-clamped variant.
    this.#apply({ syncUrl: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#urlTimer);
  }

  /** Called on slider input and change. */
  handleSlider() {
    this.#apply({ syncUrl: true });
  }

  /**
   * Moves the slider by one increment.
   *
   * @param {number} direction - -1 to step down, 1 to step up.
   */
  step(direction) {
    const { slider } = this.refs;
    const amount = Number(slider.value) + Number(this.dataset.step || 10) * direction;

    slider.value = String(this.#clamp(amount));
    this.#apply({ syncUrl: true });
  }

  /**
   * @param {number} amount
   * @returns {number}
   */
  #clamp(amount) {
    const { slider } = this.refs;
    return Math.min(Math.max(amount, Number(slider.min)), Number(slider.max));
  }

  /** @param {{ syncUrl: boolean }} options */
  #apply({ syncUrl }) {
    const { slider } = this.refs;
    const amount = this.#clamp(Number(slider.value));

    if (Number(slider.value) !== amount) slider.value = String(amount);

    const variant = this.#variants[String(Math.round(amount * 100))];

    this.#updateStepButtons(amount);

    if (!variant) {
      // A gap in the variant set. Fail loudly rather than leaving a stale id in the form.
      this.#setNotice(this.dataset.unavailableText || '');
      this.#setAddToCartEnabled(false);
      return;
    }

    this.#setNotice('');
    this.#setAddToCartEnabled(variant.available);

    slider.setAttribute('aria-valuetext', variant.money);
    this.#writeVariantId(variant.id);
    this.#writePrice(variant.money);

    if (syncUrl) this.#scheduleUrlSync(variant.id);
  }

  /** @param {number} amount */
  #updateStepButtons(amount) {
    const { slider, decrease, increase } = this.refs;
    const atMin = amount <= Number(slider.min);
    const atMax = amount >= Number(slider.max);

    // Move focus off a control before disabling it, or the keyboard user is dropped onto <body>.
    if (atMin && document.activeElement === decrease) slider.focus();
    if (atMax && document.activeElement === increase) slider.focus();

    decrease.disabled = atMin;
    increase.disabled = atMax;
  }

  /** @param {number} variantId */
  #writeVariantId(variantId) {
    const form = document.getElementById(this.dataset.productFormId || '');
    const input = form?.querySelector('input[name="id"]');

    if (input instanceof HTMLInputElement) input.value = String(variantId);
  }

  /**
   * Rewrites the price block's text.
   *
   * Scoped to this section and this product so it can never reach a recommendation card, and
   * re-queried every time because assets/product-price.js replaces the container node wholesale.
   *
   * @param {string} money
   */
  #writePrice(money) {
    const scope = document.getElementById(this.dataset.priceScope || '') ?? this.closest('.shopify-section');
    if (!scope) return;

    const selector = `product-price[data-product-id="${this.dataset.productId}"] [ref="priceContainer"] .price`;

    for (const node of scope.querySelectorAll(selector)) {
      node.textContent = money;
    }
  }

  /**
   * Only ever reverses its own change, so a button the server disabled for another reason
   * (sold out, unavailable product) is left alone.
   *
   * @param {boolean} enabled
   */
  #setAddToCartEnabled(enabled) {
    const form = document.getElementById(this.dataset.productFormId || '');
    const button = form?.querySelector('button[name="add"]');

    if (!(button instanceof HTMLButtonElement)) return;

    if (!enabled) {
      if (!button.disabled) {
        button.disabled = true;
        this.#disabledAddToCart = true;
      }
      return;
    }

    if (this.#disabledAddToCart) {
      button.disabled = false;
      this.#disabledAddToCart = false;
    }
  }

  /** @param {string} message */
  #setNotice(message) {
    const { notice, liveRegion } = this.refs;

    if (notice) {
      notice.textContent = message;
      notice.classList.toggle('hidden', message === '');
    }

    // Announced from a separate always-rendered region — the visible notice is display:none
    // until needed, and a hidden live region does not announce.
    if (liveRegion && liveRegion.textContent !== message) liveRegion.textContent = message;
  }

  /** @param {number} variantId */
  #scheduleUrlSync(variantId) {
    if (this.dataset.updateUrl !== 'true') return;

    clearTimeout(this.#urlTimer);
    this.#urlTimer = setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));

      // replaceState, never pushState — dragging must not fill the history stack.
      if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
    }, URL_SYNC_DELAY);
  }
}

if (!customElements.get('gift-card-amount-component')) {
  customElements.define('gift-card-amount-component', GiftCardAmount);
}
